import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Copy, Plus, Search } from 'lucide-react';
import { formatDate } from '@/shared/lib/format';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Skeleton } from '@/shared/ui/skeleton';
import { Tooltip } from '@/shared/ui/tooltip';
import { SortSelect } from '@/shared/components/sort-select';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { useSurveys } from '../api/use-nps';
import { SurveyCreateDialog } from './survey-create-dialog';
import { SurveyDuplicateDialog } from './survey-duplicate-dialog';
import { SURVEY_STATUS_LABEL, SURVEY_STATUS_VARIANT } from '../model/nps-utils';
import { useNpsAdmin } from '../hooks/use-nps-access';

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

export function SurveysPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, me } = useNpsAdmin();
  const { data = [], isLoading } = useSurveys();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortValue, setSortValue] = useState('recommended');
  const [createOpen, setCreateOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const scoped = isAdmin
      ? data
      : data.filter((s) => s.user?.id === me?.id || s.user?.email === me?.email);
    const byStatus =
      statusFilter === 'all'
        ? scoped
        : scoped.filter((s) => (statusFilter === 'active' ? s.status === 'ACTIVE' : s.status !== 'ACTIVE'));
    const q = search.trim().toLowerCase();
    const result = q
      ? byStatus.filter((s) => {
          const owner = `${s.user?.name ?? ''} ${s.user?.last_name ?? ''} ${s.user?.email ?? ''}`.toLowerCase();
          return s.title.toLowerCase().includes(q) || owner.includes(q);
        })
      : [...byStatus];
    if (sortValue === 'title:asc') result.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortValue === 'answers:desc') result.sort((a, b) => (b.answers_count ?? 0) - (a.answers_count ?? 0));
    else if (sortValue === 'updated:desc') result.sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
    else {
      // Recomendado: ativas primeiro, e dentro de cada grupo, mais respostas primeiro.
      result.sort((a, b) => {
        const activeDiff = Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE');
        if (activeDiff !== 0) return activeDiff;
        return (b.answers_count ?? 0) - (a.answers_count ?? 0);
      });
    }
    return result;
  }, [data, search, statusFilter, sortValue, isAdmin, me?.id, me?.email]);

  return (
    <>
      <PageHeader
        title="Pesquisas"
        description={
          isAdmin
            ? 'Crie e gerencie pesquisas de NPS de todos os clientes.'
            : 'Crie e gerencie suas pesquisas de NPS e satisfação.'
        }
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Nova pesquisa
          </Button>
        }
      />

      <div className="mb-4 space-y-3">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAdmin ? 'Buscar pesquisa ou cliente…' : 'Buscar pesquisa…'}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <SortSelect
            value={sortValue}
            onChange={setSortValue}
            className="h-10 min-w-0 flex-1 sm:w-auto sm:flex-none"
            options={[
              { value: 'recommended', label: 'Recomendado' },
              { value: 'updated:desc', label: 'Mais recentes' },
              { value: 'title:asc', label: 'Título A→Z' },
              { value: 'answers:desc', label: 'Mais respostas' },
            ]}
          />
          <SegmentedControl
            fill
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_FILTER_OPTIONS}
            className="min-w-0 flex-[1.4] sm:w-auto sm:flex-none sm:[&_button]:flex-none"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nenhuma pesquisa" description="Crie a primeira pesquisa de NPS." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((s) => {
            const ownerLabel = s.user
              ? `${s.user.name}${s.user.last_name ? ` ${s.user.last_name}` : ''}`
              : null;
            return (
              <Card
                key={s.id}
                onClick={() => navigate(`/nps/pesquisas/${s.id}`)}
                className="cursor-pointer p-5 transition-transform duration-200 hover:-translate-y-1"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                    <ClipboardList className="size-5" />
                  </span>
                  <div className="flex items-center gap-1">
                    <Tooltip content="Duplicar pesquisa">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Duplicar ${s.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDuplicatingId(s.id);
                        }}
                      >
                        <Copy className="size-4" />
                      </Button>
                    </Tooltip>
                    <Badge variant={SURVEY_STATUS_VARIANT[s.status]} className="font-normal">
                      {SURVEY_STATUS_LABEL[s.status]}
                    </Badge>
                  </div>
                </div>
                <h3 className="line-clamp-2 font-semibold">{s.title}</h3>
                <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{s.description || 'Sem descrição'}</p>
                {isAdmin && ownerLabel ? (
                  <p className="mt-2 truncate text-xs font-medium text-muted-foreground">
                    {ownerLabel}
                    {s.user?.email ? ` · ${s.user.email}` : ''}
                  </p>
                ) : null}
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Atualizada {formatDate(s.updated_at)}</p>
                  </div>
                  <Badge variant="secondary" className="font-normal">
                    {s.answers_count ?? 0} respostas
                  </Badge>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <SurveyCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => {
          queryClient.invalidateQueries({ queryKey: ['nps', 'surveys'] });
          navigate(`/nps/pesquisas/${id}`);
        }}
      />

      <SurveyDuplicateDialog
        surveyId={duplicatingId}
        onClose={() => setDuplicatingId(null)}
        onDuplicated={(id) => {
          setDuplicatingId(null);
          navigate(`/nps/pesquisas/${id}`);
        }}
      />
    </>
  );
}
